import React, { useState } from 'react';
import { Card } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Printer, ChevronDown, ChevronUp, Plus, Edit2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { InfoTooltip } from '@/components/InfoTooltip';

export const PrinterSelector = ({ printers, selected, onSelect, onCustomPrinterChange }) => {
  const [showDetails, setShowDetails] = useState(false);
  const [showCustomForm, setShowCustomForm] = useState(false);
  const [customPrinter, setCustomPrinter] = useState({
    name: 'Custom Printer',
    machine_cost: 1000,
    power_consumption_w: 100,
    lifetime_hours: 5000,
    footprint_sqm: 0.2
  });
  
  const selectedPrinter = selected === 'custom' 
    ? customPrinter 
    : printers.find(p => p.id === selected) || printers[0];
  
  const isCustom = selected === 'custom';

  const handleCustomFieldChange = (field, value) => {
    const updated = { ...customPrinter, [field]: value };
    setCustomPrinter(updated);
    if (onCustomPrinterChange) {
      onCustomPrinterChange(updated);
    }
  };

  return (
    <Card className="p-5 bg-white border-gray-200 shadow-md hover:shadow-lg transition-all">
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="p-1.5 rounded-lg bg-purple-100">
              <Printer className="h-4 w-4 text-purple-600" />
            </div>
            <Label className="text-sm font-semibold text-gray-800 flex items-center">
              Printer
              <InfoTooltip content="Select your 3D printer model. Each printer has different power consumption, depreciation rates, and footprint costs. Choose 'Custom' if your printer isn't listed." />
            </Label>
          </div>
          <div className="flex gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setShowCustomForm(!showCustomForm);
                if (!showCustomForm) {
                  onSelect('custom');
                }
              }}
              className="text-xs text-purple-600 hover:text-purple-700"
            >
              <Plus className="h-3 w-3 mr-1" />
              Custom
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowDetails(!showDetails)}
              className="text-xs text-gray-500 hover:text-gray-700"
            >
              {showDetails ? (
                <><ChevronUp className="h-3 w-3 mr-1" />Hide</>
              ) : (
                <><ChevronDown className="h-3 w-3 mr-1" />Details</>
              )}
            </Button>
          </div>
        </div>
        
        {!showCustomForm ? (
          <Select value={selected} onValueChange={onSelect}>
            <SelectTrigger data-testid="printer-selector" className="bg-gray-50 border-gray-300 hover:bg-gray-100 transition-all">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-white border-gray-200">
              {printers.map((printer) => (
                <SelectItem 
                  key={printer.id} 
                  value={printer.id}
                  className="hover:bg-gray-100 focus:bg-gray-100"
                >
                  {printer.name}
                </SelectItem>
              ))}
              <SelectItem value="custom" className="hover:bg-gray-100 focus:bg-gray-100 text-purple-600 font-medium">
                + Custom Printer
              </SelectItem>
            </SelectContent>
          </Select>
        ) : (
          <div className="p-4 rounded-xl bg-purple-50 border border-purple-200 space-y-3">
            <div className="text-sm font-semibold text-purple-700 mb-2">Custom Printer Details</div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-gray-600">Name</Label>
                <Input
                  value={customPrinter.name}
                  onChange={(e) => handleCustomFieldChange('name', e.target.value)}
                  className="h-8 text-sm mt-1"
                />
              </div>
              <div>
                <Label className="text-xs text-gray-600">Machine Cost ($)</Label>
                <Input
                  type="number"
                  value={customPrinter.machine_cost}
                  onChange={(e) => handleCustomFieldChange('machine_cost', parseFloat(e.target.value) || 0)}
                  className="h-8 text-sm mt-1"
                />
              </div>
              <div>
                <Label className="text-xs text-gray-600">Power (W)</Label>
                <Input
                  type="number"
                  value={customPrinter.power_consumption_w}
                  onChange={(e) => handleCustomFieldChange('power_consumption_w', parseFloat(e.target.value) || 0)}
                  className="h-8 text-sm mt-1"
                />
              </div>
              <div>
                <Label className="text-xs text-gray-600">Lifetime (hrs)</Label>
                <Input
                  type="number"
                  value={customPrinter.lifetime_hours}
                  onChange={(e) => handleCustomFieldChange('lifetime_hours', parseFloat(e.target.value) || 0)}
                  className="h-8 text-sm mt-1"
                />
              </div>
              <div className="col-span-2">
                <Label className="text-xs text-gray-600">Footprint (m²)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={customPrinter.footprint_sqm}
                  onChange={(e) => handleCustomFieldChange('footprint_sqm', parseFloat(e.target.value) || 0)}
                  className="h-8 text-sm mt-1"
                />
              </div>
            </div>
          </div>
        )}

        {showDetails && selectedPrinter && (
          <div className="mt-4 p-4 rounded-xl bg-gray-50 border border-gray-200 space-y-3 animate-in slide-in-from-top-2">
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div className="flex justify-between p-2 rounded-lg bg-white shadow-sm">
                <span className="text-gray-600">Cost:</span>
                <span className="text-purple-600 font-mono font-semibold">
                  ${(isCustom ? (selectedPrinter.machine_cost || 0) : (selectedPrinter.cost || 0)).toLocaleString()}
                </span>
              </div>
              <div className="flex justify-between p-2 rounded-lg bg-white shadow-sm">
                <span className="text-gray-600">Lifetime:</span>
                <span className="text-gray-900 font-mono">{(selectedPrinter.lifetime_hours || 0).toLocaleString()}h</span>
              </div>
              <div className="flex justify-between p-2 rounded-lg bg-white shadow-sm">
                <span className="text-gray-600">Power:</span>
                <span className="text-gray-900 font-mono">
                  {isCustom 
                    ? ((selectedPrinter.power_consumption_w || 0) / 1000).toFixed(2)
                    : (selectedPrinter.power_consumption_kw || 0)} kW
                </span>
              </div>
              <div className="flex justify-between p-2 rounded-lg bg-white shadow-sm">
                <span className="text-gray-600">Footprint:</span>
                <span className="text-gray-900 font-mono">
                  {isCustom ? (selectedPrinter.footprint_sqm || 0) : (selectedPrinter.footprint_m2 || 0)} m²
                </span>
              </div>
            </div>
            {!isCustom && selectedPrinter.build_volume && (
              <div className="p-2 rounded-lg bg-purple-50 border border-purple-200 text-center">
                <span className="text-xs text-gray-600 block mb-0.5">Build Volume</span>
                <span className="text-sm text-purple-700 font-mono font-semibold">{selectedPrinter.build_volume}</span>
              </div>
            )}
          </div>
        )}
      </div>
    </Card>
  );
};